"use client";

import { Image } from "@/components/ui/image";
import { Wordmark } from "@/components/brand";
import Link from "next/link";
import {
  ArrowRight,
  Building2,
  Check,
  CircleHelp,
  ClipboardCheck,
  IdCard,
  Info,
  LockKeyhole,
  ShieldCheck,
  TrendingUp,
  UsersRound,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { registrationSteps } from "@/lib/registration-steps";

const features = [
  {
    icon: TrendingUp,
    title: "Centralised oversight",
    description: "See the important status of your facility from one clear, dependable view.",
  },
  {
    icon: Building2,
    title: "Built for each facility",
    description: "Keep licensing, people, services and facility records organised in one place.",
  },
  {
    icon: ClipboardCheck,
    title: "Clearer workflows",
    description: "Move through registration, applications and follow-up tasks with less uncertainty.",
  },
  {
    icon: UsersRound,
    title: "Connected teams",
    description: "Manage the people and affiliations that support safe, consistent service delivery.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance support",
    description: "Keep the right information visible so your team can act before an issue becomes urgent.",
  },
  {
    icon: LockKeyhole,
    title: "Protected access",
    description: "Access is designed around verified administrators, secure sessions and facility rights.",
  },
];

export function LandingSessionActions({ placement }: { placement: "header" | "hero" }) {
  const { isAuthenticated, isReady, signOut } = useAuth();

  if (!isReady) {
    return (
      <>
        <Link className={placement === "header" ? "landing-button landing-button-quiet" : "landing-button landing-button-light"} href="/sign-in">Login</Link>
        <Link className={placement === "header" ? "landing-button landing-button-primary" : "landing-button landing-button-blue"} href="/sign-up/verify">Get Started</Link>
      </>
    );
  }

  if (isAuthenticated) {
    return (
      <>
        <Link className={placement === "header" ? "landing-button landing-button-quiet" : "landing-button landing-button-light"} href="/dashboard">
          Open dashboard <ArrowRight size={16} />
        </Link>
        <button className="landing-button landing-button-outline landing-action-button" type="button" onClick={signOut}>Sign out</button>
      </>
    );
  }

  return (
    <>
      <Link className={placement === "header" ? "landing-button landing-button-quiet" : "landing-button landing-button-light"} href="/sign-in">Login</Link>
      <Link className={placement === "header" ? "landing-button landing-button-primary" : "landing-button landing-button-blue"} href="/sign-up/verify">Get Started</Link>
    </>
  );
}

/* Header links mark the section in view: set on click, then kept in step with scrolling. */
const navSections = [
  { id: "about", label: "About" },
  { id: "onboarding", label: "How it works" },
] as const;
const navSectionsAfter = [{ id: "features", label: "Capabilities" }] as const;

/* `page`: the landing page tracks sections; other pages (Getting started) link back to them. */
function LandingNav({ page }: { page: "home" | "getting-started" | "faq" }) {
  const [active, setActive] = useState("");
  useEffect(() => {
    if (page !== "home") return;
    const ids = [...navSections, ...navSectionsAfter].map(({ id }) => id);
    const update = () => {
      const offset = window.innerHeight / 3;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      const passed = ids.filter((id) => (document.getElementById(id)?.getBoundingClientRect().top ?? Infinity) <= offset);
      setActive(atBottom ? ids[ids.length - 1] : passed[passed.length - 1] ?? "");
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { window.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [page]);
  const link = ({ id, label }: { id: string; label: string }) => page === "home"
    ? <a key={id} href={`#${id}`} className={active === id ? "active" : undefined} aria-current={active === id ? "location" : undefined} onClick={() => setActive(id)}>{label}</a>
    : <Link key={id} href={`/#${id}`}>{label}</Link>;
  const onGuide = page === "getting-started";
  return <nav className="landing-nav" aria-label="Main navigation">
    {navSections.map(link)}
    <Link href="/getting-started" className={onGuide ? "active" : undefined} aria-current={onGuide ? "page" : undefined}>Getting started</Link>
    {navSectionsAfter.map(link)}
  </nav>;
}

export function LandingHeader({ page = "home" }: { page?: "home" | "getting-started" | "faq" }) {
  return <header className="landing-header">
    <Link href="/" className="landing-logo" aria-label="DHA | Facility360 home">
      <Wordmark />
    </Link>
    <LandingNav page={page} />
    <div className="landing-actions"><LandingSessionActions placement="header" /></div>
  </header>;
}

export function LandingFooter() {
  return <footer className="landing-footer"><div className="landing-footer-inner"><div><Wordmark tone="inverse" /><p>Clearer facility administration for better healthcare delivery.</p></div><div className="landing-footer-support"><strong>Support</strong><span>Dial: *147#</span><span>helpdesk@dha.go.ke</span><span>Available 24/7</span></div><div className="landing-footer-links"><Link href="/terms">Terms and Conditions</Link><Link href="/privacy">Privacy Notice</Link><a href="#top">Back to top ↑</a></div></div><div className="landing-footer-bottom"><span>© 2026 facility360. All rights reserved.</span><span>Protected access for verified facility administrators.</span></div></footer>;
}

export default function LandingPage() {
  return (
    <main className="landing-page" id="top">
      <LandingHeader />

      <section className="landing-hero" aria-labelledby="landing-hero-title">
        <div className="landing-hero-inner">
          <div className="landing-hero-copy">
            <p className="landing-eyebrow">Facility administration, made clearer</p>
            <h1 id="landing-hero-title">Unified oversight for your healthcare facility</h1>
            <p className="landing-hero-lede">Manage licensing, people, daily operations and compliance from one secure, straightforward place.</p>
            <div className="landing-hero-actions"><LandingSessionActions placement="hero" /></div>
            <div className="landing-hero-note"><Check size={16} /> Designed for facility administrators</div>
          </div>
          <div className="landing-hero-visual" aria-label="Facility administration preview">
            <div className="landing-hero-frame">
              <Image src="/landing/hero-foregroung.webp" alt="Healthcare professional using a digital facility system" fill priority className="landing-hero-image" sizes="(max-width: 800px) 88vw, 420px" />
            </div>
            <div className="landing-floating-card landing-submission-card">
              <span className="landing-floating-icon"><Check size={17} /></span>
              <span><strong>Application submitted</strong><small>Your next step is clear</small></span>
            </div>
            <div className="landing-floating-card landing-staff-card">
              <span><small>Facility records</small><strong>245</strong><em>Up to date</em></span>
              <span className="landing-mini-bars"><i /><i /><i /><i /><i /></span>
            </div>
          </div>
        </div>
      </section>

      <div className="landing-proof-strip"><span>One clear view</span><span>One secure session</span><span>One connected facility</span></div>

      <section className="landing-section landing-about" id="about">
        <div className="landing-section-inner landing-two-column">
          <div className="landing-copy-block">
            <p className="landing-eyebrow landing-eyebrow-dark">About facility360</p>
            <h2>Everything your facility team needs to stay in control.</h2>
            <p>facility360 is a central place for facility administrators to manage health professionals, licensing activities, daily operations and administrative tasks with confidence.</p>
            <p>It brings the important work together, strengthens visibility and helps teams deliver consistent, compliant healthcare services.</p>
            <Link className="landing-text-link" href="/sign-up/verify">Start with your facility <ArrowRight size={16} /></Link>
          </div>
          <div className="landing-artwork landing-about-artwork"><Image src="/landing/about.webp" alt="Facility360 connects facility teams and healthcare services" fill sizes="(max-width: 800px) 90vw, 520px" /></div>
        </div>
      </section>

      <section className="landing-section landing-onboarding" id="onboarding">
        <div className="landing-section-inner">
          <div className="landing-section-heading">
            <p className="landing-eyebrow landing-eyebrow-dark">Facility administrator onboarding</p>
            <h2>A simple way to get started</h2>
            <p>Start with your identity, choose your facility and continue into a workspace built around your responsibilities.</p>
            <Link className="landing-text-link" href="/getting-started">Read the Facility360 getting started guide <ArrowRight size={16} /></Link>
          </div>
          <div className="landing-onboarding-grid">
            <div className="landing-screen-stage" aria-label="Preview of the registration screen">
              <div className="landing-screen" aria-hidden="true">
                <div className="landing-screen-bar"><i /><i /><i /><span><LockKeyhole size={10} />facility360 · Register your facility</span></div>
                <div className="landing-screen-body">
                  <div className="landing-screen-rail">
                    <Wordmark tone="inverse" size="sm" />
                    <div><p className="landing-screen-rail-title">Register Your Facility</p><p className="landing-screen-rail-lede">About 5 minutes. Keep your ID, email and phone nearby.</p></div>
                    <ol>{registrationSteps.map(({ title, detail }, index) => <li key={title} className={index === 0 ? "current" : undefined}><span>{index + 1}</span><div><strong>{title}</strong><small>{detail}</small></div></li>)}</ol>
                  </div>
                  <div className="landing-screen-form">
                    <p className="landing-screen-help"><CircleHelp size={12} />Need help? <strong>Contact support</strong></p>
                    <div className="landing-screen-fields">
                      <p className="landing-preview-eyebrow">Step 1 of 7</p>
                      <h3>Verify Your Identity</h3>
                      <p>Start as the facility administrator. We’ll match your ID against the facility registry.</p>
                      <div className="landing-preview-segmented"><span className="active">National ID</span><span>Passport</span><span>Alien ID</span></div>
                      <div className="landing-preview-input"><small>ID number</small><span><IdCard size={13} />Enter your ID number</span></div>
                      <div className="landing-screen-callout"><Info size={14} /><span><strong>Administrator Access Required</strong>You must be listed as the facility owner in the registry before you can register it here.</span></div>
                      <div className="landing-preview-button">Continue <ArrowRight size={14} /></div>
                      <p className="landing-screen-switch">Already registered? <strong>Sign in</strong></p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="landing-floating-card landing-stage-badge"><span className="landing-floating-icon"><ShieldCheck size={17} /></span><span><strong>Registry verified</strong><small>Ownership is checked before you continue</small></span></div>
            </div>
            <div className="landing-step-actions landing-onboarding-actions"><LandingSessionActions placement="header" /></div>
          </div>
        </div>
      </section>

      <section className="landing-section landing-features" id="features">
        <div className="landing-section-inner">
          <div className="landing-section-heading"><p className="landing-eyebrow landing-eyebrow-dark">Capabilities</p><h2>Designed for operational clarity and clinical excellence</h2><p>Clear information, dependable status and a more consistent way for facility teams to work.</p></div>
          <div className="landing-feature-grid">{features.map(({ icon: Icon, title, description }) => <article className="landing-feature-card" key={title}><span className="landing-feature-icon"><Icon size={24} /></span><h3>{title}</h3><p>{description}</p></article>)}</div>
        </div>
      </section>

      <section className="landing-cta">
        <div className="landing-cta-copy"><span className="landing-cta-mark"><Check size={18} /></span><p className="landing-eyebrow">Ready to get started?</p><h2>Bring your facility work into one clear view.</h2><p>Set up access today and give your team a simpler way to stay on top of facility responsibilities.</p><div className="landing-hero-actions"><LandingSessionActions placement="header" /></div></div><div className="landing-cta-image"><Image src="/landing/get-started.webp" alt="Healthcare team working together" fill sizes="(max-width: 800px) 90vw, 540px" /></div>
      </section>

      <LandingFooter />
    </main>
  );
}
